import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Sparkles, 
  CheckCircle, 
  Loader2,
  Copy,
  ArrowRight,
  AlertCircle
} from 'lucide-react';

import { apiClient, formatP0Error } from '../lib/api';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [accountInfo, setAccountInfo] = useState(null);
  const [pollingCount, setPollingCount] = useState(0);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (pollingCount >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.get(`/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Get account setup info
        const setupResponse = await apiClient.get(`/payments/setup-account/${sessionId}`);
        setAccountInfo(setupResponse.data);
      } else if (response.data.status === 'expired') {
        setStatus('error');
      } else {
        // Continue polling
        setPollingCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Payment status error:', error);
      setPollingCount(prev => prev + 1);
      if (pollingCount < maxAttempts - 1) {
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  const handleSetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setSettingPassword(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      // Login with temp password then update
      const loginResponse = await apiClient.post('/auth/login', {
        email: accountInfo.email,
        password: accountInfo.temp_password
      });

      const token = loginResponse.data.token;

      // Update password - need to set token for this call
      await apiClient.put('/auth/me', 
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Mot de passe défini avec succès !');
      
      // Store token and redirect
      localStorage.setItem('token', token);
      navigate('/dashboard');
    } catch (error) {
      console.error('Password setup error:', error);
      toast.error(formatP0Error(error) || 'Erreur lors de la configuration du mot de passe');
    } finally {
      setSettingPassword(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Sparkles className="w-12 h-12 text-blue-600" />
          </div>
          
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
              <CardTitle>Vérification du paiement...</CardTitle>
              <CardDescription>Veuillez patienter quelques instants</CardDescription>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <CardTitle className="text-green-700">Paiement réussi !</CardTitle>
              <CardDescription>Bienvenue sur AI Marketing Engine</CardDescription>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
              <CardTitle className="text-red-700">Erreur de paiement</CardTitle>
              <CardDescription>Une erreur est survenue lors de la vérification</CardDescription>
            </>
          )}
        </CardHeader>

        <CardContent>
          {status === 'success' && accountInfo && (
            <div className="space-y-6">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-medium text-green-800 mb-2">Votre compte a été créé</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-green-700">Email :</span>
                    <div className="flex items-center gap-2">
                      <code className="bg-white px-2 py-1 rounded">{accountInfo.email}</code>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(accountInfo.email)}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">Définissez votre mot de passe</h4>
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nouveau mot de passe</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
                <Button 
                  className="w-full" 
                  onClick={handleSetPassword}
                  disabled={settingPassword}
                >
                  {settingPassword ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Configuration...</>
                  ) : (
                    <>Accéder au tableau de bord <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <p className="text-center text-slate-600">
                Si votre paiement a été effectué, veuillez contacter le support.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => navigate('/pricing')}>
                  Retour aux tarifs
                </Button>
                <Button className="flex-1" onClick={() => window.location.href = 'mailto:contact@ai-marketing-engine.com'}>
                  Contacter le support
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
